import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import { X, Globe, Plus, Trash2, ChevronDown, ChevronUp, Info, Braces } from 'lucide-react';
import { FormField, HttpMethod, CUSTOMER_VARIABLES } from '../../types/form';

const HTTP_COLOR = '#EA580C';
const CUSTOMER_VAR_COLOR = '#0D9488';

const HTTP_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

interface HttpRequestConfigDialogProps {
  open: boolean;
  onClose: () => void;
  field: FormField | null;
  onSave: (field: FormField) => void;
  existingFieldIds: string[];
  allFields: FormField[];
  hasVerificationBefore?: boolean;
}

const HttpRequestConfigDialog: React.FC<HttpRequestConfigDialogProps> = ({
  open,
  onClose,
  field,
  onSave,
  existingFieldIds,
  allFields,
  hasVerificationBefore = false,
}) => {
  const theme = useTheme();
  const isEditing = !!field;
  const urlRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [fieldName, setFieldName] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState<HttpMethod>('GET');
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [infoExpanded, setInfoExpanded] = useState(false);

  // Fields available for reference (all prior fields)
  const currentOrder = field?.order || Infinity;
  const availableFields = allFields.filter(f => f.order < currentOrder);

  useEffect(() => {
    if (open) {
      if (field) {
        setFieldName(field.fieldId);
        setFieldId(field.fieldId);
        setLabel(field.label);
        setUrl(field.httpRequestConfig?.url || '');
        setMethod(field.httpRequestConfig?.method || 'GET');
        setHeaders(field.httpRequestConfig?.headers || []);
        setBody(field.httpRequestConfig?.body || '');
      } else {
        setFieldName('');
        setFieldId('');
        setLabel('');
        setUrl('');
        setMethod('GET');
        setHeaders([]);
        setBody('');
      }
      setError('');
    }
  }, [open, field]);

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    if (!isEditing) {
      const generatedId = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 30);
      setFieldId(generatedId);
    }
  };

  const headerValueRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  const insertAtCursor = (
    ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const el = ref.current;
    if (el) {
      const start = el.selectionStart ?? value.length;
      setter(prev => prev.substring(0, start) + value + prev.substring(start));
      setTimeout(() => {
        el.focus();
        const pos = start + value.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setter(prev => prev + value);
    }
  };

  const insertFieldReference = (refFieldId: string, target: 'url' | 'body') => {
    const text = `{${refFieldId}}`;
    if (target === 'url') {
      insertAtCursor(urlRef, text, setUrl);
    } else {
      insertAtCursor(bodyRef, text, setBody);
    }
  };

  const handleDropOnField = (
    e: React.DragEvent,
    ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data) {
      insertAtCursor(ref, data, setter);
    }
  };

  const handleDropOnHeaderValue = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data) {
      const el = headerValueRefs.current.get(index);
      const currentValue = headers[index]?.value || '';
      const start = el?.selectionStart ?? currentValue.length;
      const newValue = currentValue.substring(0, start) + data + currentValue.substring(start);
      handleHeaderChange(index, 'value', newValue);
      if (el) {
        setTimeout(() => {
          el.focus();
          const pos = start + data.length;
          el.setSelectionRange(pos, pos);
        }, 0);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const handleHeaderChange = (index: number, key: 'key' | 'value', value: string) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [key]: value };
    setHeaders(newHeaders);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!fieldName.trim()) {
      setError('Field Name is required');
      return;
    }
    if (!fieldId.trim()) {
      setError('Field ID is required');
      return;
    }
    if (!url.trim()) {
      setError('URL is required');
      return;
    }
    if (!isEditing && existingFieldIds.includes(fieldId)) {
      setError('Field ID already exists. Please use a unique identifier.');
      return;
    }

    const savedField: FormField = {
      fieldId: fieldId.trim(),
      label: fieldName.trim(),
      fieldType: 'http_request',
      required: false,
      httpRequestConfig: {
        url: url.trim(),
        method,
        headers: headers.filter(h => h.key.trim()),
        body: body.trim() || undefined,
      },
      order: field?.order || 0,
    };

    onSave(savedField);
  };

  const showBody = method === 'POST' || method === 'PUT' || method === 'PATCH';

  const [formatError, setFormatError] = useState('');

  const handleFormatBody = () => {
    setFormatError('');
    if (!body.trim()) return;

    // Replace {fieldId} and {customer_name} placeholders with temporary tokens to preserve them
    // Handle both quoted "{fieldId}" and unquoted {fieldId} variants
    const placeholders: string[] = [];
    const tokenized = body.replace(/["']?\{([a-zA-Z_][a-zA-Z0-9_]*)\}["']?/g, (_match, id) => {
      const token = `"__PLACEHOLDER_${placeholders.length}__"`;
      placeholders.push(id);
      return token;
    });

    // Try to fix common JSON issues: unquoted keys, single quotes, trailing commas
    let fixedJson = tokenized
      // Replace single quotes with double quotes
      .replace(/'/g, '"')
      // Add quotes around unquoted keys (word characters before a colon)
      .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, '$1"$2"$3')
      // Remove trailing commas before } or ]
      .replace(/,(\s*[}\]])/g, '$1');

    try {
      const parsed = JSON.parse(fixedJson);
      let formatted = JSON.stringify(parsed, null, 2);

      // Restore {fieldId} placeholders
      placeholders.forEach((id, index) => {
        formatted = formatted.replace(`"__PLACEHOLDER_${index}__"`, `{${id}}`);
      });

      setBody(formatted);
    } catch {
      setFormatError('Could not format: invalid JSON structure. Please check your syntax.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Globe size={20} color={HTTP_COLOR} />
          <Typography variant="h6">
            {isEditing ? 'Edit HTTP Request' : 'Add HTTP Request'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Field Name"
            value={fieldName}
            onChange={e => handleFieldNameChange(e.target.value)}
            fullWidth
            required
            disabled={isEditing}
            placeholder="e.g., fetch_pricing"
            helperText="Internal name for this HTTP request"
          />

          {/* Info Panel */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.info.main}30`,
              borderRadius: 1,
              backgroundColor: `${theme.palette.info.main}08`,
            }}
          >
            <Box
              onClick={() => setInfoExpanded(prev => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Info size={16} color={theme.palette.info.main} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: theme.palette.info.main, flex: 1 }}
              >
                Using field references
              </Typography>
              {infoExpanded ? (
                <ChevronUp size={14} color={theme.palette.info.main} />
              ) : (
                <ChevronDown size={14} color={theme.palette.info.main} />
              )}
            </Box>
            <Collapse in={infoExpanded}>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ lineHeight: 1.7 }}
                >
                  Use <code>{'{field_id}'}</code> in the URL, body, or header values to insert a
                  field's value. The agent will substitute actual values at runtime.
                </Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Available Fields */}
          {availableFields.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}
              >
                Available field references
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {availableFields.map(f => (
                  <Chip
                    key={f.fieldId}
                    label={`{${f.fieldId}}`}
                    size="small"
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.setData('text/plain', `{${f.fieldId}}`);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => insertFieldReference(f.fieldId, showBody ? 'body' : 'url')}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      backgroundColor: `${HTTP_COLOR}10`,
                      color: HTTP_COLOR,
                      borderColor: `${HTTP_COLOR}35`,
                      cursor: 'grab',
                      '&:hover': {
                        backgroundColor: `${HTTP_COLOR}20`,
                        borderColor: HTTP_COLOR,
                      },
                      '&:active': { cursor: 'grabbing' },
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Customer Variables (only if verification is before this field) */}
          {hasVerificationBefore && (
            <Box
              sx={{
                border: `1px solid ${CUSTOMER_VAR_COLOR}30`,
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                  color: CUSTOMER_VAR_COLOR,
                }}
              >
                Customer Variables
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {CUSTOMER_VARIABLES.map(cv => (
                  <Chip
                    key={cv.id}
                    label={cv.placeholder}
                    size="small"
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.setData('text/plain', cv.placeholder);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => {
                      if (showBody) {
                        insertAtCursor(bodyRef, cv.placeholder, setBody);
                      } else {
                        insertAtCursor(urlRef, cv.placeholder, setUrl);
                      }
                    }}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      backgroundColor: `${CUSTOMER_VAR_COLOR}10`,
                      color: CUSTOMER_VAR_COLOR,
                      borderColor: `${CUSTOMER_VAR_COLOR}35`,
                      cursor: 'grab',
                      '&:hover': {
                        backgroundColor: `${CUSTOMER_VAR_COLOR}20`,
                        borderColor: CUSTOMER_VAR_COLOR,
                      },
                      '&:active': { cursor: 'grabbing' },
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.62rem', mt: 0.5, display: 'block' }}
              >
                Available because this field is after customer verification
              </Typography>
            </Box>
          )}

          {/* Method + URL */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Method</InputLabel>
              <Select
                value={method}
                label="Method"
                onChange={e => setMethod(e.target.value as HttpMethod)}
              >
                {HTTP_METHODS.map(m => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{ flex: 1 }}
              onDrop={e => handleDropOnField(e, urlRef, setUrl)}
              onDragOver={handleDragOver}
            >
              <TextField
                label="URL"
                value={url}
                onChange={e => setUrl(e.target.value)}
                fullWidth
                required
                placeholder="https://api.example.com/endpoint/{field_id}"
                inputRef={urlRef}
                size="small"
              />
            </Box>
          </Box>

          {/* Headers */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button size="small" startIcon={<Plus size={14} />} onClick={handleAddHeader}>
                Add Header
              </Button>
            </Box>
            {headers.map((header, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="Key"
                  value={header.key}
                  onChange={e => handleHeaderChange(index, 'key', e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="Content-Type"
                />
                <Box
                  sx={{ flex: 1 }}
                  onDrop={e => handleDropOnHeaderValue(e, index)}
                  onDragOver={handleDragOver}
                >
                  <TextField
                    label="Value"
                    value={header.value}
                    onChange={e => handleHeaderChange(index, 'value', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="application/json"
                    inputRef={el => {
                      if (el) headerValueRefs.current.set(index, el);
                    }}
                  />
                </Box>
                <IconButton size="small" color="error" onClick={() => handleRemoveHeader(index)}>
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Body (only for POST/PUT/PATCH) */}
          {showBody && (
            <Box onDrop={e => handleDropOnField(e, bodyRef, setBody)} onDragOver={handleDragOver}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  JSON body. Use {'{fieldId}'} to reference form field values.
                </Typography>
                <Button
                  size="small"
                  startIcon={<Braces size={14} />}
                  onClick={handleFormatBody}
                  disabled={!body.trim()}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Format JSON
                </Button>
              </Box>
              {formatError && (
                <Alert severity="warning" sx={{ mb: 1, py: 0 }} onClose={() => setFormatError('')}>
                  <Typography variant="caption">{formatError}</Typography>
                </Alert>
              )}
              <TextField
                label="Request Body"
                value={body}
                onChange={e => {
                  setBody(e.target.value);
                  setFormatError('');
                }}
                fullWidth
                multiline
                rows={6}
                placeholder='{"key": "{field_id}", "data": "value"}'
                inputRef={bodyRef}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: HTTP_COLOR,
            '&:hover': { backgroundColor: '#C2410C' },
          }}
        >
          {isEditing ? 'Update HTTP Request' : 'Add HTTP Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HttpRequestConfigDialog;
